// Shipmondo v1: the packing screen, shipments, label issues, the shipping
// price table and the shipment settings. Mirrors the backend's
// /v1/ecommerce/shipping/* responses.

export type DeliveryMethod = "delivery" | "pickup" | "own_delivery";

export const DELIVERY_METHODS: DeliveryMethod[] = [
  "delivery",
  "pickup",
  "own_delivery",
];

export const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  delivery: "Delivery",
  pickup: "Pickup",
  own_delivery: "Own delivery",
};

export type Carrier = "gls" | "postnord";

export const CARRIERS: Carrier[] = ["gls", "postnord"];

export const CARRIER_LABELS: Record<Carrier, string> = {
  gls: "GLS",
  postnord: "PostNord",
};

export type ShipmentKind = "carrier" | "pickup" | "own_delivery";

export type ShipmentState =
  | "booked"
  | "sent"
  | "delivered"
  | "ready"
  | "collected"
  | "handed"
  | "cancelled";

export const SHIPMENT_STATE_LABELS: Record<ShipmentState, string> = {
  booked: "Booked",
  sent: "Sent",
  delivered: "Delivered",
  ready: "Ready for pickup",
  collected: "Collected",
  handed: "Handed to driver",
  cancelled: "Cancelled",
};

export type ShipmentSource = "erp" | "shipmondo_ui";

export type AddressType = {
  company: string | null;
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  email: string | null;
  phone: string | null;
};

export type ShipmentLineType = {
  orderItemId: number;
  name: string | null;
  quantity: number;
};

export type ShipmentParcelType = {
  id: number;
  quantity: number;
  weightGrams: number;
  estimateGrams: number | null;
  trackingNo: string | null;
};

export type ShipmentType = {
  id: number;
  orderId: number | null;
  orderNo: string | null;
  kind: ShipmentKind;
  state: ShipmentState;
  source: ShipmentSource;
  carrier: Carrier | null;
  carrierCode: string | null;
  productCode: string | null;
  ownAgreement: boolean | null;
  shipmondoId: number | null;
  shipmondoReference: string | null;
  shipmondoSourceType: string | null;
  trackingNo: string | null;
  trackingUrl: string | null;
  hasLabel: boolean;
  priceExVat: number | null;
  extraChargesExVat: number | null;
  ownDeliveryCost: number | null;
  replacesId: number | null;
  monitorState: string | null;
  bookedAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readyAt: string | null;
  collectedAt: string | null;
  handedAt: string | null;
  cancelledAt: string | null;
  emailSentAt: string | null;
  createdById: number | null;
  parcels: ShipmentParcelType[];
  lines: ShipmentLineType[];
  canCancel: boolean;
  canMarkSent: boolean;
};

/** A label made in the Shipmondo UI that matched no order. `receiver` is
 *  Shipmondo's own receiver object (snake_case). */
export type UnmatchedShipmentType = ShipmentType & {
  receiver: Record<string, string | null> | null;
};

export type IssueSeverity = "high" | "medium" | "low";
export type IssueState = "open" | "cleared" | "ok";

export type LabelIssueType = {
  id: number;
  type: string;
  severity: IssueSeverity;
  state: IssueState;
  detail: string | null;
  note: string | null;
  shipmentId: number | null;
  shipmondoId: number | null;
  trackingNo: string | null;
  orderId: number | null;
  orderNo: string | null;
  okById: number | null;
  okAt: string | null;
  clearedAt: string | null;
  created: string | null;
};

export const LABEL_ISSUE_LABELS: Record<string, string> = {
  repurchase_source_active: "Repurchase while the source label is active",
  repurchase: "Repurchase instead of Cancel / Rebook",
  booked_outside_erp: "Label booked outside the ERP",
  unmatched: "No matching order",
  carrier_label_on_pickup: "Carrier label on a pickup order",
  duplicate_active_label: "More than one active label",
  label_on_cancelled_order: "Label on a cancelled order",
  weight_over_paid_band: "Weight over the paid band",
  extra_charge: "Carrier extra charge",
  cost_above_charged: "Cost above the charged shipping",
  receiver_differs: "Receiver differs from the order",
  not_scanned: "No scan after booking",
};

export type PackingQueueRowType = {
  orderId: number;
  orderNo: string;
  status: string;
  customer: string;
  deliveryMethod: DeliveryMethod;
  lines: ShipmentLineType[];
  lineCount: number;
  quantity: number;
  isSplit: boolean;
  hasActiveShipments: boolean;
  /** Lines left to pack that are ready (or have no production). */
  readyLines: number;
  deadline: string | null;
  openIssues: number;
};

/** "ready": the last QC step is done. "none": no production (bought in). */
export type ProductionState = "ready" | "in_production" | "none";

export const PRODUCTION_STATE_LABELS: Record<ProductionState, string> = {
  ready: "Ready",
  in_production: "In production",
  none: "No production",
};

export type PackingLineType = {
  orderItemId: number;
  name: string;
  productId: number | null;
  ordered: number;
  shipped: number;
  remaining: number;
  unitWeightKg: number;
  production: ProductionState;
};

export type PriceBandType = {
  fromKg: number;
  toKg: number;
  price: number;
};

export type PackingOrderType = {
  orderId: number;
  orderNo: string;
  status: string;
  customer: string;
  customerEmail: string | null;
  poNumber: string | null;
  shippingAddress: AddressType | null;
  deliveryMethod: DeliveryMethod;
  shippingMethod: string | null;
  isCustomShippingMethod: boolean;
  shippingExVat: number | null;
  shippingPriceColumn: string | null;
  shippingBandFromKg: number | null;
  shippingBandToKg: number | null;
  weightKg: number | null;
  lines: PackingLineType[];
  estimateGrams: number | null;
  defaultBoxWeightGrams: number;
  defaultCarrier: Carrier;
  ownDeliveryCost: number;
  printEnabled: boolean;
  bands: PriceBandType[];
  shipments: ShipmentType[];
  issues: LabelIssueType[];
};

export type LineInput = { orderItemId: number; quantity: number };
export type ParcelInput = { weightGrams: number; quantity?: number };

export type BookInput = {
  lines: LineInput[];
  parcels: ParcelInput[];
  carrier?: Carrier;
};

export type RebookInput = {
  parcels: ParcelInput[];
  carrier?: Carrier;
};

export type LabelFileType = {
  base64: string;
  fileFormat: string;
};

export type PackingSlipType = {
  shipmentId: number;
  orderNo: string;
  poNumber: string | null;
  customer: string;
  shippingAddress: AddressType | null;
  kind: ShipmentKind;
  carrier: Carrier | null;
  trackingNo: string | null;
  date: string | null;
  lines: (ShipmentLineType & { ordered: number | null })[];
};

export type PriceColumn = "gls" | "postnord" | "own_delivery";

export const PRICE_COLUMNS: PriceColumn[] = ["gls", "postnord", "own_delivery"];

export const PRICE_COLUMN_LABELS: Record<PriceColumn, string> = {
  gls: "GLS",
  postnord: "PostNord",
  own_delivery: "Own delivery",
};

export type ShippingPriceType = {
  id: number;
  column: PriceColumn;
  weightFromKg: number;
  weightToKg: number;
  price: number;
  priceInclTax: number;
  validFrom: string | null;
  validTo: string | null;
  expectedCost: number | null;
};

export type ShippingPriceInput = {
  column: PriceColumn;
  weightFromKg: number;
  weightToKg: number;
  price: number;
  validFrom: string | null;
  validTo: string | null;
  expectedCost: number | null;
};

// Shipmondo's label_format names. An unknown name comes back as an A4 PDF.
export type LabelFormat =
  | "10x19_zpl"
  | "10x19_pdf"
  | "10x19_png"
  | "a4_pdf"
  | "compact_zpl"
  | "compact_pdf"
  | "compact_png";

export const LABEL_FORMATS: LabelFormat[] = [
  "10x19_zpl",
  "10x19_pdf",
  "10x19_png",
  "a4_pdf",
  "compact_zpl",
  "compact_pdf",
  "compact_png",
];

// The ones a label printer takes (print_at and print jobs).
export const PRINT_FORMATS: LabelFormat[] = [
  "10x19_zpl",
  "10x19_pdf",
  "a4_pdf",
];

export type LabelPrinterType = {
  host_name: string;
  printer_name: string;
  label_format: string;
};

export type ShipmentSettingsType = {
  label_printer: LabelPrinterType | null;
  print_enabled: boolean;
  label_format: LabelFormat;
  default_carrier: Carrier;
  own_agreement: Record<Carrier, boolean>;
  default_box_weight_grams: number;
  own_delivery_cost: number;
  weight_limit_kg: number;
  sender: {
    name: string;
    attention: string;
    address1: string;
    address2: string;
    zipcode: string;
    city: string;
    country_code: string;
    email: string;
    mobile: string;
  };
  notifications: { email_nt: boolean; sms_nt: boolean };
  queue_entry: "processing" | "production_done";
  event_payload_days: number;
  event_retention_days: number;
  not_scanned_working_days: number;
  monitor_poll: boolean;
};

export type PrinterType = {
  hostName: string | null;
  printerName: string | null;
  name: string | null;
  device: string | null;
  labelFormat: string | null;
  defaultPrinter: boolean;
};
